/* Step2Form.js */
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import DateIcon from "../../../imgs/date-icon.svg";
import { trackProductLifecycle } from "../../../network/fetch";
import ProgressBar from "../../../components/ProgressBar/ProgressBar";
import TutorialPopup from "../../../components/TutorialPopup/TutorialPopup";
import State from "../State";
import ArrowIcon from "../../../imgs/arrow-down-icon.svg";

const Step2Form = () => {
  const wait = (ms) =>
    new Promise((resolve) => {
      console.log("=== `waiting ${ms}ms` === Step1Form.js === key: 366434 ===");
      console.dir(`waiting ${ms}ms`, { depth: null, colors: true });
      console.log("=================================");
      setTimeout(resolve, ms);
    });

  const navigate = useNavigate();

  const total_steps = 5; // Nombre total d'étapes
  const current_step = 2; // Étape actuelle

  const batteryDateInputRef = useRef(null);
  const electronicDateInputRef = useRef(null);

  const [txUri, setTxUri] = useState(null);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [docIsSaved, setDocIsSaved] = useState(false);

  // Fonction pour générer un identifiant unique
  const generateUniqueId = (nbrBlocs = 3, nbrChars = 6) => {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const getRandomChars = (length) => {
      let result = "";
      const charactersLength = characters.length;
      for (let i = 0; i < length; i++) {
        result += characters.charAt(
          Math.floor(Math.random() * charactersLength)
        );
      }
      return result;
    };
    let blocs = [];
    for (let i = 0; i < nbrBlocs; i++) {
      blocs.push(getRandomChars(nbrChars));
    }
    return blocs.join("-");
  };

  // État pour les champs du formulaire
  const [formData, setFormData] = useState({
    vehicleUniqueId: "",
    vehicleUniqueIdConfidential: false,
    batteryUniqueId: "",
    batteryUniqueIdConfidential: false,
    batteryAssemblyDate: new Date().toISOString().split("T")[0],
    batteryAssemblyDateConfidential: false,
    electronicSystemProduct: "Système Électronique 1",
    electronicSystemProductConfidential: false,
    electronicSystemAssemblyDate: new Date().toISOString().split("T")[0],
    electronicSystemAssemblyDateConfidential: false,
  });

  // États pour les options des listes déroulantes
  const [batteryUniqueIdOptions, setBatteryUniqueIdOptions] = useState([]);
  const [electronicSystemProductOptions, setElectronicSystemProductOptions] =
    useState([]);

  // Initialisation des valeurs lors du montage du composant
  useEffect(() => {
    // Générer l'identifiant unique du véhicule
    console.log("=== State === Step2Form.js === key: 970206 ===");
    console.dir(State, { depth: null, colors: true });
    console.log("=================================");

    const vehicleId = generateUniqueId(3, 6);

    State.vehicleData.productId = vehicleId;

    // Récupérer l'identifiant unique et la date de fabrication de la batterie depuis l'état
    const batteryUniqueId = State.batteryProductData.productId || "";
    const batteryAssemblyDate =
      State.batteryProductData.manufacturingDate ||
      new Date().toISOString().split("T")[0];

    // Définir les options pour le champ batteryUniqueId
    setBatteryUniqueIdOptions([batteryUniqueId]);

    // Définir les options pour le champ electronicSystemProduct
    setElectronicSystemProductOptions([
      "Système Électronique 1",
      "Système Électronique 2",
      "Système Électronique 3",
    ]);

    setFormData((prevData) => ({
      ...prevData,
      vehicleUniqueId: vehicleId,
      batteryUniqueId: batteryUniqueId,
      batteryAssemblyDate: batteryAssemblyDate,
    }));
  }, []);

  // État pour gérer les erreurs et le chargement
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const dppNextStep = (e) => {
    e.preventDefault();
    navigate("/dpp/step3", {
      replace: true,
    });
    return;
  };

  const handleNextTutorialStep = (e) => {
    e.preventDefault();
    if (tutorialStep < tutorialSteps.length - 1) {
      setTutorialStep(tutorialStep + 1);
    } else {
      setTutorialStep(-1); // Toutes les étapes sont terminées
    }
  };

  const handleCloseTutorial = (e) => {
    e.preventDefault();
    setTutorialStep(-1);
  };

  const [tutorialSteps, setTutorialSteps] = useState([
    {
      content: (
        <>
          <p className="tutorial-title">Encodez vos données (1/3)</p>
          <p>
            En tant que constructeur de véhicules électriques, vous avez acheté
            un lot de batterie que vous souhaitez assembler sur vos
            voitures. Vous êtes responsable de l’encodage des composantes du
            véhicule, dont la batterie. Vous complétez les informations dans
            votre système habituel. Ces données sont enregistrées sur la
            blockchain afin de les rendre disponibles à tous les acteurs qui en
            ont besoin.
          </p>
        </>
      ),
      position: { top: "100px", left: "50%", transform: "translateX(-50%)" },
    },
    {
      content: (
        <>
          <p className="tutorial-title">Sélection de la batterie (2/3)</p>
          <p>
            Choisissez la batterie précédemment enregistrée pour l’intégrer
            à votre véhicule.
          </p>
        </>
      ),
      showArrow: "left",
      position: { top: "373px", left: "55%", width: "500px" },
    },
    {
      content: (
        <>
          <p className="tutorial-title">Sélection du système (3/3)</p>
          <p>
            A titre démonstratif, vous devez également choisir le système
            électronique à intégrer à votre véhicule.
          </p>
        </>
      ),
      showArrow: "left",
      position: { top: "655px", left: "55%", width: "350px" },
      closeLabel: "hidden",
    },
    // Ajoutez plus d'étapes si nécessaire
  ]);

  const submitedStep = [
    {
      content: (
        <>
          <p className="tutorial-title">
            Vos informations ont été enregistrées avec succès
          </p>
          <p>
            Vous avez enregistré les informations du véhicule. Ces données sont
            désormais accessibles via la blockchain pour les autres acteurs de
            la chaîne. Cliquez sur le bouton ci-dessous pour consulter les
            informations brutes disponibles sur la blockchain.
          </p>
        </>
      ),
      closeLabel: "hidden",
      nextLabel: "Etape suivante",
      handleNextTutorialStep: (e) => {
        dppNextStep(e);
      },
      position: {
        top: "320px",
        left: "50%",
        transform: "translateX(-50%)",
        width: "950px",
      },
    },
  ];

  // Gestion du changement d'entrée
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData({
        ...formData,
        [name]: checked,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  // Gestion de la soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const productId = State.batteryProductData.productId; // Récupérer l'ID du produit précédent

    const lifecycleData = {
      eventType: "assembly",
      eventDate: new Date().toISOString(),
      vehicleUniqueId: formData.vehicleUniqueId,
      vehicleUniqueIdConfidential: formData.vehicleUniqueIdConfidential,
      batteryUniqueId: formData.batteryUniqueId,
      batteryUniqueIdConfidential: formData.batteryUniqueIdConfidential,
      batteryAssemblyDate: formData.batteryAssemblyDate,
      batteryAssemblyDateConfidential: formData.batteryAssemblyDateConfidential,
      electronicSystemProduct: formData.electronicSystemProduct,
      electronicSystemProductConfidential:
        formData.electronicSystemProductConfidential,
      electronicSystemAssemblyDate: formData.electronicSystemAssemblyDate,
      electronicSystemAssemblyDateConfidential:
        formData.electronicSystemAssemblyDateConfidential,
    };

    try {
      const response = await trackProductLifecycle(productId, lifecycleData);

      if (response.success) {
        console.log("=== response === Step2Form.js === key: 107356 ===");
        console.dir(response, { depth: null, colors: true });
        console.log("=================================");

        State.vehicleData = { ...response.lifecycleData, ...response.data };
        State.vehicleData.productId = response.lifecycleData.vehicleUniqueId;

        console.log("=== State === Step2Form.js === key: 088586 ===");
        console.dir(State, { depth: null, colors: true });
        console.log("=================================");

        // Gérer le succès
        setTxUri(response.data.tx_uri);
        setDocIsSaved(true);
        setTutorialSteps(submitedStep);
        setTutorialStep(0);
      } else {
        // Gérer les erreurs renvoyées par l'API
        setError(
          "Erreur lors du suivi du cycle de vie du produit. Veuillez réessayer."
        );
        console.error("Erreur API:", response.error);
      }
    } catch (err) {
      setError("Une erreur est survenue. Veuillez réessayer.");
      console.error("Erreur de soumission:", err);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return (
      formData.vehicleUniqueId &&
      formData.batteryUniqueId &&
      formData.batteryAssemblyDate &&
      formData.electronicSystemProduct &&
      formData.electronicSystemAssemblyDate
    );
  };

  return (
    <form onSubmit={handleSubmit} className="container form-2">
      <div className="form-heading-wrapper">
        <ProgressBar total_steps={total_steps} current_step={current_step} />
      </div>
      {tutorialStep >= 0 && (
        <TutorialPopup
          content={tutorialSteps[tutorialStep].content}
          position={tutorialSteps[tutorialStep].position}
          isVisible={tutorialStep >= 0}
          onNext={
            tutorialSteps[tutorialStep].handleNextTutorialStep ||
            handleNextTutorialStep
          }
          onClose={handleCloseTutorial}
          txUrl={txUri}
          showArrow={tutorialSteps[tutorialStep].showArrow || false}
          tmpNextLabel={tutorialSteps[tutorialStep].nextLabel}
          tmpCloseLabel={tutorialSteps[tutorialStep].closeLabel}
        />
      )}
      <div className="fiels-main-wrapper">
        <div className="fields-main">
          <div className="fields-heading">
            <h3>Véhicule électrique</h3>
          </div>
          <div className="fields-main-switch-main">
            <div className="spacer"></div>
            <p className="form-switchs-label">Données confidentielles</p>
          </div>
          <div className="fields-main-item">
            <div className="form-field">
              <label htmlFor="vehicleUniqueId" className="form-label">
                Identifiant unique
              </label>
              <input
                type="text"
                className="form-control"
                id="vehicleUniqueId"
                name="vehicleUniqueId"
                value={formData.vehicleUniqueId}
                readOnly
                required
              />
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="vehicleUniqueIdConfidential"
                name="vehicleUniqueIdConfidential"
                checked={formData.vehicleUniqueIdConfidential}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="fields-heading">
            <h3>Batterie</h3>
          </div>
          <div className="fields-main-item">
            <div className="form-field">
              <label htmlFor="batteryUniqueId" className="form-label">
                Identifiant unique
              </label>
              <select
                className="form-control"
                id="batteryUniqueId"
                name="batteryUniqueId"
                value={formData.batteryUniqueId}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  Veuillez sélectionner un identifiant
                </option>
                {batteryUniqueIdOptions.map((option, index) => (
                  <option key={index} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <span className="date-icon">
                <img src={ArrowIcon} alt="Arrow Icon"></img>
              </span>
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="batteryUniqueIdConfidential"
                name="batteryUniqueIdConfidential"
                checked={formData.batteryUniqueIdConfidential}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="fields-main-item">
            <div className="form-field">
              <label htmlFor="batteryAssemblyDate" className="form-label">
                Date d’assemblage
              </label>
              <input
                type="date"
                className="form-control"
                id="batteryAssemblyDate"
                name="batteryAssemblyDate"
                value={formData.batteryAssemblyDate}
                onChange={handleChange}
                ref={batteryDateInputRef}
                required
              />
              <span
                className="date-icon"
                onClick={() =>
                  batteryDateInputRef.current &&
                  batteryDateInputRef.current.showPicker &&
                  batteryDateInputRef.current.showPicker()
                }
              >
                <img src={DateIcon} alt="Icône de date" />
              </span>
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="batteryAssemblyDateConfidential"
                name="batteryAssemblyDateConfidential"
                checked={formData.batteryAssemblyDateConfidential}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="fields-heading">
            <h3>Système électronique</h3>
          </div>
          <div className="fields-main-item">
            <div className="form-field">
              <label htmlFor="electronicSystemProduct" className="form-label">
                Sélection du produit
              </label>
              <select
                className="form-control"
                id="electronicSystemProduct"
                name="electronicSystemProduct"
                value={formData.electronicSystemProduct}
                onChange={handleChange}
                required
              >
                {electronicSystemProductOptions.map((option, index) => (
                  <option key={index} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <span className="date-icon">
                <img src={ArrowIcon} alt="Arrow Icon"></img>
              </span>
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="electronicSystemProductConfidential"
                name="electronicSystemProductConfidential"
                checked={formData.electronicSystemProductConfidential}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="fields-main-item">
            <div className="form-field">
              <label
                htmlFor="electronicSystemAssemblyDate"
                className="form-label"
              >
                Date d’assemblage
              </label>
              <input
                type="date"
                className="form-control"
                id="electronicSystemAssemblyDate"
                name="electronicSystemAssemblyDate"
                value={formData.electronicSystemAssemblyDate}
                onChange={handleChange}
                ref={electronicDateInputRef}
                required
              />
              <span
                className="date-icon"
                onClick={() =>
                  electronicDateInputRef.current &&
                  electronicDateInputRef.current.showPicker &&
                  electronicDateInputRef.current.showPicker()
                }
              >
                <img src={DateIcon} alt="Icône de date" />
              </span>
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="electronicSystemAssemblyDateConfidential"
                name="electronicSystemAssemblyDateConfidential"
                checked={formData.electronicSystemAssemblyDateConfidential}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
        {error && <div className="error-message">{error}</div>}
        <div className="btn-wrapper">
          {!loading && !docIsSaved && (
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!isFormValid() || loading}
            >
              Enregistrer l'assemblage
            </button>
          )}
          {loading && (
            <div className="spinner-wrapper">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </form>
  );
};

export default Step2Form;
